import React, { useState } from 'react';
import { XIcon, ChromeIcon, SafariIcon, EdgeIcon, AppleIcon, WindowsIcon, AndroidIcon, ShareIcon, AddToHomeScreenIcon, InstallIcon } from 'shared-icons';

interface PwaInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type OsTab = 'desktop' | 'mobile';

const PwaInstructionsModal: React.FC<PwaInstructionsModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<OsTab>('desktop');

  if (!isOpen) return null;

  const InstructionStep: React.FC<{ icon: React.ReactNode; text: React.ReactNode }> = ({ icon, text }) => (
    <li className="flex items-center gap-4">
        <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400 rounded-lg flex items-center justify-center">
            {icon}
        </div>
        <span className="text-slate-700 dark:text-slate-300">{text}</span>
    </li>
  );

  return (
    <div
      className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="pwa-instructions-title"
      style={{ animation: 'modal-fade-in 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
    >
      <div
        className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl shadow-2xl w-full max-w-2xl p-6"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'modal-content-pop 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 id="pwa-instructions-title" className="text-xl font-bold text-slate-900 dark:text-white">
            Install TeezSpeed App
          </h2>
          <button onClick={onClose} className="text-gray-400 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white text-3xl leading-none" aria-label="Close modal">&times;</button>
        </div>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">
          For faster access, you can install TeezSpeed on your device. It works just like a native app and can be used offline.
        </p>

        <div className="border-b border-gray-200 dark:border-slate-700 mb-6">
          <nav className="-mb-px flex space-x-4" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('desktop')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'desktop' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-slate-300'}`}
              aria-current={activeTab === 'desktop' ? 'page' : undefined}
            >
              Desktop
            </button>
            <button
              onClick={() => setActiveTab('mobile')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'mobile' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-slate-300'}`}
              aria-current={activeTab === 'mobile' ? 'page' : undefined}
            >
              Mobile
            </button>
          </nav>
        </div>

        {activeTab === 'desktop' && (
          <div className="space-y-6">
            <div>
              <h3 className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200 mb-3">
                <ChromeIcon /> <EdgeIcon /> On Chrome or Edge
              </h3>
              <ol className="space-y-3 pl-2">
                <InstructionStep icon={<InstallIcon width={20} height={20} />} text={<>Look for the <strong>Install</strong> icon in the address bar.</>} />
                <InstructionStep icon={<strong className="text-lg">2</strong>} text="Click it, then select 'Install' from the prompt." />
              </ol>
            </div>
            <div className="border-t border-gray-200 dark:border-slate-700/50 pt-6">
              <h3 className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200 mb-3">
                <AppleIcon /> On macOS (Safari)
              </h3>
               <ol className="space-y-3 pl-2">
                 <InstructionStep icon={<strong className="text-lg">1</strong>} text={<>In the menu bar, go to <strong>File</strong> &rarr; <strong>Add to Dock</strong>.</>} />
                 <InstructionStep icon={<strong className="text-lg">2</strong>} text="Follow the on-screen instructions to add the app." />
              </ol>
            </div>
          </div>
        )}
        
        {activeTab === 'mobile' && (
          <div className="space-y-6">
            <div>
              <h3 className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200 mb-3">
                <AppleIcon /> On iOS & iPadOS (Safari)
              </h3>
               <ol className="space-y-3 pl-2">
                <InstructionStep icon={<ShareIcon width={20} height={20} />} text={<>Tap the <strong>Share</strong> button in the browser toolbar.</>} />
                <InstructionStep icon={<AddToHomeScreenIcon width={20} height={20} />} text={<>Scroll down and tap <strong>'Add to Home Screen'</strong>.</>} />
                <InstructionStep icon={<strong className="text-lg">3</strong>} text="Confirm by tapping 'Add'." />
              </ol>
            </div>
             <div className="border-t border-gray-200 dark:border-slate-700/50 pt-6">
              <h3 className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200 mb-3">
                <AndroidIcon /> On Android (Chrome)
              </h3>
               <ol className="space-y-3 pl-2">
                 <InstructionStep icon={<strong className="text-lg">1</strong>} text="Look for the 'Add TeezSpeed to Home screen' banner at the bottom." />
                 <InstructionStep icon={<strong className="text-lg">2</strong>} text={<>If you don't see it, tap the <strong>three dots</strong> menu icon and select <strong>Install app</strong>.</>} />
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PwaInstructionsModal;